/* eslint-disable no-undef */

/**
 * WebReaderService
 *
 * Centralizes all ReadSpeaker WebReader interactions.
 *
 * WHY LANGUAGE SYNC IS REQUIRED:
 * WebReader's translation popup plays back audio using the TTS engine for the
 * TARGET language. If the reader language is still set to the source language
 * (e.g. "en") when the popup opens, WebReader tries to read the translated text
 * with an English voice and shows "cannot read aloud in this language."
 * Calling setReaderLanguage(lang) BEFORE triggering translation ensures the
 * correct TTS voice is active when the popup's play button is pressed.
 */

const WebReaderService = {
  _readyCallbacks: [],
  _isReady: false,

  /**
   * Initialize - call once on app load.
   * Listens for ReadSpeaker's rsapiReady event which fires when the
   * WebReader API is fully available.
   */
  init() {
    // rsapiReady fires when rspkr and rspkr.ui are fully initialized
    window.addEventListener('rsapiReady', () => {
      this._isReady = true;
      this._readyCallbacks.forEach((fn) => fn());
      this._readyCallbacks = [];
    });

    // If already ready (e.g. script loaded before init called), mark ready now
    if (window.rspkr?.ui) {
      this._isReady = true;
    }
  },

  /**
   * Returns true if ReadSpeaker WebReader is available and initialized.
   */
  isAvailable() {
    return !!(window.rspkr?.ui);
  },

  /**
   * Queues an action to run when WebReader is ready.
   * If already ready, runs immediately.
   */
  whenReady(fn) {
    if (this.isAvailable()) {
      fn();
    } else {
      this._readyCallbacks.push(fn);
    }
  },

  /**
   * Set the WebReader reading language.
   * This MUST be called before translation so the TTS voice matches the
   * target language. Without this, WebReader uses the source language voice
   * and shows "cannot read aloud in this language" in the translation popup.
   *
   * @param {string} lang - Language code e.g. "nl_nl", "fr_fr"
   */
  setReaderLanguage(lang) {
    if (!lang || !window.rspkr?.ui) return;

    try {
      // rspkr.ui.settings is the runtime settings object
      // Setting the language here changes which TTS voice is used for playback
      if (typeof window.rspkr.ui.settings?.set === 'function') {
        window.rspkr.ui.settings.set('general.lang', lang);
      }
      // Also set on the active player if one exists
      const player = window.rspkr.ui.getActivePlayer?.();
      if (player && typeof player.setLanguage === 'function') {
        player.setLanguage(lang);
      }
      // Fallback: set directly on rspkr config
      if (window.rspkr.cfg?.item) {
        window.rspkr.cfg.item('general.lang', lang);
      }
    } catch (e) {
      console.warn('[WebReaderService] setReaderLanguage failed:', e);
    }
  },

  /**
   * Normalize a language code to the format ReadSpeaker expects for TTS.
   * RS requires the full locale format with underscores (nl_nl, fr_fr) not
   * short codes (nl, fr) or hyphenated codes (nl-NL, fr-FR).
   * ttsSupported('nl') returns false but ttsSupported('nl_nl') returns true.
   */
  normalizeLanguageCode(lang) {
    if (!lang) return lang;

    // App selectable locales → ReadSpeaker lang codes (xx_yy).
    // Voice selection still comes from RS account config for that lang.
    const localeMap = {
      // Amharic
      'am': 'am_et',
      // Arabic
      'ar': 'ar_ar', 'ar-ar': 'ar_ar',
      // Bengali
      'bn': 'bn_in',
      // Cebuano
      'ceb': 'ceb_ph',
      // Czech
      'cs': 'cs_cz', 'cs-cz': 'cs_cz',
      // Danish
      'da': 'da_dk', 'da-dk': 'da_dk',
      // German
      'de': 'de_de', 'de-de': 'de_de', 'de-at': 'de_at',
      // Greek
      'el': 'el_gr',
      // English
      'en': 'en_us', 'en-us': 'en_us', 'en-gb': 'en_uk',
      // Spanish — keep regional variants distinct
      'es': 'es_mx', 'es-mx': 'es_mx', 'es-es': 'es_es', 'es-ar': 'es_ar',
      // Persian/Farsi
      'fa': 'fa_ir',
      // Finnish
      'fi': 'fi_fi', 'fi-fi': 'fi_fi',
      // Tagalog (Filipino)
      'fil': 'fil_ph',
      // French
      'fr': 'fr_fr', 'fr-fr': 'fr_fr', 'fr-ca': 'fr_ca',
      // Gujarati
      'gu': 'gu_in',
      // Hausa
      'ha': 'ha_ng',
      // Hawaiian
      'haw': 'haw_us',
      // Hindi
      'hi': 'hi_in', 'hi-in': 'hi_in',
      // Hmong
      'hmn': 'hmn_us', 'hmn-us': 'hmn_us',
      // Haitian Creole
      'ht': 'ht_ht',
      // Hungarian
      'hu': 'hu_hu', 'hu-hu': 'hu_hu',
      // Indonesian (app code "in")
      'in': 'id_id', 'id': 'id_id', 'id-id': 'id_id',
      // Italian
      'it': 'it_it', 'it-it': 'it_it',
      // Hebrew (app code "iw")
      'iw': 'he_il', 'he': 'he_il', 'he-il': 'he_il',
      // Japanese
      'ja': 'ja_jp', 'ja-jp': 'ja_jp',
      // Khmer
      'km': 'km_kh',
      // Kannada
      'kn': 'kn_in',
      // Korean
      'ko': 'ko_kr', 'ko-kr': 'ko_kr',
      // Lao
      'lo': 'lo_la',
      // Latvian
      'lv': 'lv_lv',
      // Maori
      'mi': 'mi_nz',
      // Marathi
      'mr': 'mr_in',
      // Myanmar / Burmese
      'my': 'my_mm',
      // Norwegian (Bokmål)
      'nb': 'no_nb', 'nb-no': 'no_nb', 'no': 'no_nb',
      // Nepali
      'ne': 'ne_np',
      // Dutch
      'nl': 'nl_nl', 'nl-nl': 'nl_nl',
      // Oromo
      'om': 'om_et',
      // Punjabi
      'pa': 'pa_in',
      // Polish
      'pl': 'pl_pl', 'pl-pl': 'pl_pl',
      // Pashto
      'ps': 'ps_af',
      // Portuguese
      'pt': 'pt_pt', 'pt-pt': 'pt_pt', 'pt-br': 'pt_br',
      // Romanian
      'ro': 'ro_ro', 'ro-ro': 'ro_ro',
      // Russian
      'ru': 'ru_ru', 'ru-ru': 'ru_ru',
      // Kinyarwanda
      'rw': 'rw_rw',
      // Slovak
      'sk': 'sk_sk', 'sk-sk': 'sk_sk',
      // Samoan
      'sm': 'sm_ws',
      // Somali
      'so': 'so_so',
      // Swedish
      'sv': 'sv_se', 'sv-se': 'sv_se',
      // Swahili
      'sw': 'sw_ke',
      // Tamil
      'ta': 'ta_in',
      // Telugu
      'te': 'te_in',
      // Thai
      'th': 'th_th', 'th-th': 'th_th',
      // Turkish
      'tr': 'tr_tr',
      // Ukrainian
      'uk': 'uk_ua',
      // Urdu
      'ur': 'ur_pk',
      // Vietnamese
      'vi': 'vi_vn', 'vi-vn': 'vi_vn',
      // Chinese
      'zh': 'zh_cn', 'zh-cn': 'zh_cn', 'zh-tw': 'zh_tw'
    };

    const normalized = localeMap[lang.toLowerCase()];
    if (normalized) return normalized;

    // Fallback: convert hyphen to underscore and lowercase (e.g. fr-FR → fr_fr)
    if (lang.includes('-')) return lang.replace('-', '_').toLowerCase();

    return lang.toLowerCase();
  },

  /**
   * Build the Listen button href for the current language.
   * Voice is left to ReadSpeaker account configuration (do not hardcode).
   */
  buildListenHref(customerId, lang, hostname = window.location.hostname) {
    const rsLang = this.normalizeLanguageCode(lang) || 'en_us';
    const encUrl = encodeURI(hostname);
    return `//app-na.readspeaker.com/cgi-bin/rsent?customerid=${customerId}&lang=${rsLang}&readclass=tRdbl&url=${encUrl}`;
  },

  /**
   * Ensure customerid is set in the correct location.
   * ReadSpeaker reads from rspkr.c.data.params.customerid to build API URLs.
   * After a page refresh this value can be lost, causing cid=null and 401 errors.
   */
  ensureCustomerId() {
    const customerId = window.rsConf?.general?.customerid;
    if (customerId && window.rspkr?.c?.data) {
      if (!window.rspkr.c.data.params) {
        window.rspkr.c.data.params = {};
      }
      window.rspkr.c.data.params.customerid = customerId;
      return true;
    }
    return false;
  },

  /**
   * translateSelection
   *
   * WHY setReaderLanguage IS CALLED FIRST:
   * WebReader's translation popup plays back audio using the TTS engine for the
   * TARGET language. If the reader language is still set to the source language
   * (e.g. "en") when the popup opens, WebReader tries to read the translated text
   * with an English voice and shows "cannot read aloud in this language."
   * Calling setReaderLanguage(lang) BEFORE triggering translation ensures the
   * correct TTS voice is active when the popup's play button is pressed.
   *
   * WHY THE PLAY BUTTON ONLY WORKS ONCE:
   * After the player finishes reading, it reaches an "end of content" state.
   * The translate dialog stays open but the player is exhausted.
   * To replay, we must re-call translate() to re-open the dialog and re-prepare
   * the player with fresh content. We hook the play button via MutationObserver
   * to detect when the user clicks play on an already-finished player.
   *
   * @param {string} lang - Target language code e.g. "nl_nl"
   * @param {Range|null} savedRange - Previously saved DOM selection range
   */
  translateSelection(lang, savedRange) {
    if (!window.rspkr?.ui?.Tools?.Translation?.translate) {
      console.warn('[WebReaderService] Translation tool not available');
      return false;
    }

    // Normalize to full locale format - ttsSupported('nl') = false but ttsSupported('nl_nl') = true
    const normalizedLang = this.normalizeLanguageCode(lang);

    this.ensureCustomerId();
    this.setReaderLanguage(normalizedLang);

    // Step 3: Restore the selection range saved before the button click
    if (savedRange) {
      try {
        if (savedRange.startContainer?.parentNode) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange.cloneRange());
        }
      } catch (e) {
        console.warn('[WebReaderService] Failed to restore selection range:', e);
      }
    }

    // Step 4: Call WebReader translate with the normalized language code
    try {
      window.rspkr.ui.Tools.Translation.translate(normalizedLang);
      this._hookTranslationDialogPlayButton(normalizedLang, savedRange);
      return true;
    } catch (e) {
      console.warn('[WebReaderService] Translation call failed:', e);
      try {
        window.rspkr.ui.Tools.Translation.translate(normalizedLang);
        return true;
      } catch (e2) {
        console.warn('[WebReaderService] Translation fallback also failed:', e2);
        return false;
      }
    }
  },

  /**
   * Hook the play button in the RS translation dialog.
   * After the first playback ends, clicking play would fail with "cannot read
   * aloud in this language" because the player is exhausted. We intercept the
   * play button click and re-call translate() to re-prepare the player.
   */
  _hookTranslationDialogPlayButton(lang, savedRange) {
    // Wait for the RS dialog to appear in the DOM
    const observer = new MutationObserver(() => {
      const dialog = document.querySelector('.rslightbox[data-rsmode="translate"], .rs-dialog-translate, [class*="rslightbox"]');
      if (!dialog) return;

      // Find the play button inside the dialog
      const playBtn = dialog.querySelector('.rsbtn_play, .rspopup_btn_play, [class*="play"]');
      if (!playBtn || playBtn._wrHooked) return;

      playBtn._wrHooked = true;
      observer.disconnect();

      playBtn.addEventListener('click', (e) => {
        const player = window.rspkr?.ui?.getActivePlayer?.();
        // If player is in an ended/idle state, re-trigger translation
        if (!player || player.isPlaying?.() === false) {
          e.stopImmediatePropagation();
          e.preventDefault();
          // Re-set language and re-translate to reinitialize the player
          this.setReaderLanguage(lang);
          if (savedRange) {
            try {
              if (savedRange.startContainer?.parentNode) {
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(savedRange.cloneRange());
              }
            } catch (_e) { /* ignore */ }
          }
          try {
            window.rspkr.ui.Tools.Translation.translate(lang);
          } catch (_e) {
            try { window.rspkr.ui.Tools.Translation.translate(); } catch (_e2) { /* ignore */ }
          }
        }
      }, true);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    // Clean up observer after 10 seconds if dialog never appeared
    setTimeout(() => observer.disconnect(), 10000);
  },

  /**
   * Reduce a locale to the base language the dictionary API expects on `l=`.
   * Unlike TTS, the dictionary takes `es`, not `es_es`.
   */
  dictionaryLanguageCode(lang) {
    const raw = lang || document.documentElement.lang || 'en';
    return String(raw).trim().toLowerCase().split(/[-_]/)[0] || 'en';
  },

  /**
   * Lookup a word in the WebReader dictionary.
   *
   * @param {Range|null} savedRange - Previously saved DOM selection range
   * @param {string} [lang] - Locale of the content being read, defaults to the document language
   */
  dictionaryLookup(savedRange, lang) {
    const dictTool = window.rspkr?.ui?.Tools?.Dictionary;
    const dictFn = dictTool?.dictionary || dictTool?.lookup || dictTool?.translate || dictTool?.dictionaryLookup;

    if (typeof dictFn !== 'function') {
      console.warn('[WebReaderService] Dictionary tool not available');
      return false;
    }

    this.ensureCustomerId();

    if (savedRange) {
      try {
        if (savedRange.startContainer?.parentNode) {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange.cloneRange());
        }
      } catch (e) {
        console.warn('[WebReaderService] Failed to restore selection range:', e);
      }
    }

    try {
      dictFn(this.dictionaryLanguageCode(lang));
      return true;
    } catch (e) {
      console.warn('[WebReaderService] Dictionary call failed:', e);
      try {
        dictFn();
        return true;
      } catch (e2) {
        console.warn('[WebReaderService] Dictionary fallback failed:', e2);
        return false;
      }
    }
  }
};

export default WebReaderService;
